import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getJobCard, getTasks, getSignatures, getATRItems, syncATRItems } from "../services/jobcardApi";
import { listGrns, deleteGrn } from "../services/grnApi";
import type { Grn } from "../types/grn";
import GrnForm from "../components/grn/GrnForm";
import GrnFinalizeModal from "../components/grn/GrnFinalizeModal";
import GrnViewModal from "../components/grn/GrnViewModal";
import GrnTable from "../components/grn/GrnTable";
import { ArrowLeft, Printer, ChevronDown, ChevronRight, Edit } from "lucide-react";
import { JobCard, Task, Signature, AssetToRepair, TaskEquipment } from "../types/jobcard";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import api from "../services/api";
import TaskImageModal from "../components/modals/TaskImageModal";
import StockRequestModal from "../components/modals/StockRequestModal";
import type { Equipment } from "../components/modals/StockRequestModal";
import TaskImagesGallery from "../components/TaskImagesGallery";
import ImageDebugInfo from '../components/ImageDebugInfo';
import { getTaskImageUrl, getSignatureImageUrl } from '../config';
import ATRItemsEmbedded from '../components/ATRItemsEmbedded';
import { useAuth } from '../utils/auth';

// import config from '../config';

interface ApiError {
    response?: {
        data: {
            message?: string;
            errors?: Record<string, string[]>;
            missing_fields?: string[];
            warnings?: string[];
            task_count?: number;
            incomplete_atr_count?: number;
        };
    };
    message?: string;
    code?: string; // For timeout
}

// Helper function to check if a job card is from ATERA (contains "ticket" in order number)
const isAteraJobCard = (jobCard: JobCard | null): boolean => {
  return jobCard?.order_number ? jobCard.order_number.toLowerCase().includes('ticket') : false;
};

export default function ViewJobCardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jobCard, setJobCard] = useState<JobCard | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isStockRequestModalOpen, setIsStockRequestModalOpen] = useState(false);
  const [atrItems, setAtrItems] = useState<AssetToRepair[]>([]);
  const [isLoadingATRItems, setIsLoadingATRItems] = useState(true);
  const [expandedEquipment, setExpandedEquipment] = useState<Record<number, boolean>>({});
  const [isSyncingATRItems, setIsSyncingATRItems] = useState(false);
  const { user, isAdmin } = useAuth();
  const [canUndo, setCanUndo] = useState(false);
  const [undoExpiresAt, setUndoExpiresAt] = useState<string | null>(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [grns, setGrns] = useState<Grn[]>([]);
  const [showGrnForm, setShowGrnForm] = useState(false);
  const [editingGrn, setEditingGrn] = useState<Grn | null>(null);
  const [finalizingGrn, setFinalizingGrn] = useState<Grn | null>(null);
  const [viewingGrn, setViewingGrn] = useState<Grn | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const jobCardRes = await getJobCard(Number(id));
        const tasksRes = await getTasks(Number(id));
        const signaturesRes = await getSignatures(Number(id));

        const jobCardData = jobCardRes.data.data;
        setJobCard(jobCardData);
        setTasks(tasksRes.data);
        setSignatures(signaturesRes.data.data);
        
        // Fetch ATR items
        try {
          setIsLoadingATRItems(true);
          const atrRes = await getATRItems(Number(id));
          setAtrItems(atrRes.data.data.data || []);
        } catch {
          // console.error('Error fetching ATR items:', error);
        } finally {
          setIsLoadingATRItems(false);
        }

        // Load GRNs
        try {
          const grnList = await listGrns(Number(id));
          setGrns(grnList);
        } catch {
          // ignore grn load errors
        }

        // Background ATR sync - only if job card has order number
        if (jobCardData.order_number) {
          try {
            await syncATRItems(Number(id));
            // After sync, refresh ATR items in background
            const atrRes = await getATRItems(Number(id));
            setAtrItems(atrRes.data.data.data || []);
          } catch {
            // Silent fail, do not block page load
          }
        }
      } catch {
        showErrorToast("Error loading job card");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Check if Undo Link is available for this job card
  useEffect(() => {
    if (!jobCard) return;
    // Only check if the job card is linked
    if (jobCard.linked_from_job_card_id) {
      api.get(`/jobcards/${jobCard.id}/can-undo-link`).then(res => {
        setCanUndo(res.data.can_undo);
        setUndoExpiresAt(res.data.undo_expires_at);
      }).catch(() => {
        setCanUndo(false);
        setUndoExpiresAt(null);
      });
    }
  }, [jobCard]);

  const refreshJobCard = async () => {
    try {
      setIsLoading(true);
      const tasksRes = await getTasks(Number(id));

      // Force a fresh fetch by clearing the cache
      const freshJobCardRes = await api.get(`/jobcards/${id}`, {
        headers: { "Cache-Control": "no-cache" },
      });

      setJobCard(freshJobCardRes.data.data);
      setTasks(tasksRes.data);
    } catch {
      showErrorToast("Error refreshing job card");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      setIsPrinting(true);
      const response = await api.get(`/jobcards/${id}/print`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `JobCard-${jobCard?.job_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      showErrorToast("Error generating PDF");
    } finally {
      setIsPrinting(false);
    }
  };

  const tasksWithImages = tasks.filter((task) => task.pictures && task.pictures.length > 0);

  // const handleImageClick = (taskIndex: number) => {
  //   const imageIndex = tasksWithImages.findIndex(
  //     (t) => t.id === tasks[taskIndex].id
  //   );
  //   setCurrentImageIndex(imageIndex);
  //   setIsImageModalOpen(true);
  // };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      Math.min(prev + 1, tasksWithImages.length - 1)
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => Math.max(prev - 1, 0));
  };

  const toggleEquipmentSection = (taskId: number) => {
    setExpandedEquipment(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  // Add sync handler
  const handleSyncATRItems = async () => {
    if (!id || !jobCard?.order_number) {
      showErrorToast('Cannot sync ATR items: Job card has no order number');
      return;
    }
    try {
      setIsSyncingATRItems(true);
      const syncRes = await syncATRItems(Number(id));
      
      // Handle different response codes
      const responseData = syncRes?.data;
      if (responseData?.code === 'NO_ASSETS_FOUND') {
        showSuccessToast('No ATR items found for this purchase order.');
        setAtrItems([]);
      } else if (responseData?.code === 'PO_NOT_FOUND') {
        if (responseData?.deleted_items?.job_card) {
          // Job card was deleted, show success message and redirect
          showSuccessToast('Job card deleted as purchase order was not found. Redirecting to job cards list...');
          setTimeout(() => {
            navigate('/jobcards');
          }, 2000);
          return; // Don't continue with fetching ATR items
        } else {
          showErrorToast(responseData?.message || 'Purchase order not found');
          setAtrItems([]);
        }
      } else if (responseData?.code === 'PO_NOT_INTERNAL') {
        showSuccessToast(responseData?.message || 'Purchase order is not internal');
        setAtrItems([]);
      } else if (responseData?.message?.toLowerCase().includes('synced successfully')) {
        const message = responseData?.summary?.data_source === 'purchase_order_only' 
          ? 'ATR items synced successfully (from PO data only)'
          : 'ATR items synced successfully';
        showSuccessToast(message);
      } else {
        showSuccessToast(responseData?.message || 'ATR items synced successfully');
      }
      // Refresh ATR items
      const atrRes = await getATRItems(Number(id));
      setAtrItems(atrRes.data.data.data || []);
    } catch (e: unknown) {
      const error = e as ApiError;
      const msg = error?.response?.data?.message || error?.message || '';
      if (msg.toLowerCase().includes('no assets found') || 
          msg.toLowerCase().includes('purchase order is not internal')) {
        showSuccessToast('No ATR items found for this order.');
        setAtrItems([]);
      } else if (msg.toLowerCase().includes('purchase order not found')) {
        if (msg.toLowerCase().includes('deleted as they were orphaned')) {
          // Job card was deleted, show success message and redirect
          showSuccessToast('Job card deleted as purchase order was not found. Redirecting to job cards list...');
          setTimeout(() => {
            navigate('/jobcards');
          }, 2000);
          return; // Don't continue with fetching ATR items
        } else {
          showErrorToast('Purchase order not found. Please verify the order number.');
          setAtrItems([]);
        }
      } else {
        showErrorToast('Failed to sync ATR items');
      }
    } finally {
      setIsSyncingATRItems(false);
    }
  };

  // Handler for Undo Link (now opens modal)
  const handleUndoLink = () => {
    // Only open the modal, do not trigger undo here
    setShowUndoModal(true);
  };

  // Confirm undo action (only called from modal)
  const confirmUndoLink = async () => {
    if (!jobCard) return;
    setIsUndoing(true);
    try {
      await api.post(`/jobcards/${jobCard.id}/undo-link`);
      showSuccessToast('Job card link undone successfully');
      setShowUndoModal(false);
      await refreshJobCard();
    } catch {
      showErrorToast('Failed to undo job card link');
    } finally {
      setIsUndoing(false);
    }
  };

  const handleGrnSave = async () => {
    // Refresh GRN list
    try {
      const grnList = await listGrns(Number(id));
      setGrns(grnList);
    } catch {
      // ignore errors
    }
    
    setShowGrnForm(false);
    setEditingGrn(null);
    showSuccessToast(editingGrn ? 'GRN updated successfully' : 'GRN created successfully');
  };

  const handleGrnCancel = () => {
    setShowGrnForm(false);
    setEditingGrn(null);
  };

  const handleEditGrn = (grn: Grn) => {
    if (grn.status === 'finalized' && !isAdmin) {
      showErrorToast('Cannot edit a finalized GRN');
      return;
    }
    setEditingGrn(grn);
    setShowGrnForm(true);
  };

  const handleFinalizeGrn = (grn: Grn) => {
    setFinalizingGrn(grn);
  };

  const handleViewGrn = (grn: Grn) => {
    setViewingGrn(grn);
  };

  const handleGrnFinalized = async () => {
    // Refresh GRN list
    try {
      const grnList = await listGrns(Number(id));
      setGrns(grnList);
    } catch {
      // ignore errors
    }
    
    setFinalizingGrn(null);
    showSuccessToast('GRN finalized successfully');
  };

  const handleDeleteGrn = async (grn: Grn) => {
    if (grn.status === 'finalized' && !isAdmin) {
      showErrorToast('Cannot delete a finalized GRN');
      return;
    }

    if (!grn.id) {
      showErrorToast('GRN ID not found');
      return;
    }

    const confirmMessage = grn.status === 'finalized' 
      ? `Are you sure you want to delete finalized GRN ${grn.grn_number || 'Draft'}? This action cannot be undone.`
      : `Are you sure you want to delete GRN ${grn.grn_number || 'Draft'}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    if (!id) {
      showErrorToast('Job card ID not found');
      return;
    }

    try {
      await deleteGrn(grn.id!);
      // Refresh GRN list
      const grnList = await listGrns(Number(id));
      setGrns(grnList);
      showSuccessToast('GRN deleted successfully');
    } catch (error) {
      console.error('Error deleting GRN:', error);
      showErrorToast('Failed to delete GRN');
    }
  };

  // Determine if the Undo Link button should be shown
  const showUndoLinkButton = (() => {
    if (!jobCard || !user) return false;
    // Only show if this job card is the result of a link (has linked_from_job_card_id)
    if (!jobCard.linked_from_job_card_id) return false;
    // Admins can always undo
    if (isAdmin) return true;
    // Job card owner can undo if within the allowed window
    if (user.id === jobCard.user_id && canUndo) return true;
    return false;
  })();

  // Determine edit/print availability (resilient to type mismatches)
  // Edit: owner if open; admin if closed
  // Print: anyone if closed
  const canEditJobCard = (() => {
    if (!jobCard || !user) return false;

    const closedValue = (jobCard as unknown as { closed?: unknown }).closed;
    const isClosed = closedValue === true || closedValue === 1 || closedValue === '1' || closedValue === 'true';
    const isOwner = String(user.id) === String(jobCard.user_id);

    if (!isClosed && isOwner) return true; // owner editing open job card
    if (isClosed && isAdmin) return true;  // admin editing closed job card
    return false;
  })();

  const canPrintJobCard = (() => {
    if (!jobCard) return false;
    const closedValue = (jobCard as unknown as { closed?: unknown }).closed;
    const isClosed = closedValue === true || closedValue === 1 || closedValue === '1' || closedValue === 'true';
    return isClosed; // print available to all when closed
  })();

  if (isLoading)
    return (
      <div className="text-center py-8 text-gray-900 dark:text-white">
        Loading...
      </div>
    );
  if (!jobCard)
    return (
      <div className="text-center py-8 text-gray-900 dark:text-white">
        Job card not found
      </div>
    );

  return (
    <div className="container mx-auto px-4 py-8 bg-white dark:bg-gray-900">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate("/jobcards")}
          className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>
        {/* Buttons: Print shows when closed (for all). Edit shows for owner if open, admin if closed. */}
        <div className="flex items-center gap-3">
          {canEditJobCard && (
            <button
              onClick={() => navigate(`/jobcards/edit/${id}`)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 transition-colors duration-200"
            >
              <Edit className="w-5 h-5 mr-2" />
              Edit Job Card
            </button>
          )}
          {canPrintJobCard && (
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <Printer className="w-5 h-5 mr-2" />
              {isPrinting ? 'Generating PDF...' : 'Print Job Card'}
            </button>
          )}
        </div>
      </div>

      {/* Undo Link Button Section - moved to top of page */}
      {showUndoLinkButton && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg flex items-center justify-between">
          <div>
            <span className="font-semibold text-yellow-800 dark:text-yellow-200">This job card was linked/merged from a PO job card.</span>
            {isAdmin ? (
              <span className="ml-2 text-xs text-yellow-700 dark:text-yellow-300">(Admins can always undo)</span>
            ) : (
              // Only show undoExpiresAt if it is a valid string
              !!undoExpiresAt && (
                <span className="ml-2 text-xs text-yellow-700 dark:text-yellow-300">
                  Undo available until: {undoExpiresAt ? new Date(undoExpiresAt).toLocaleString() : ''}
                </span>
              )
            )}
          </div>
          <button
            onClick={handleUndoLink}
            disabled={isUndoing}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 ml-4"
          >
            {isUndoing ? 'Undoing...' : 'Undo Link'}
          </button>
        </div>
      )}

      {/* Undo Link Confirmation Modal */}
      {showUndoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowUndoModal(false)}></div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4 relative z-10">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Undo Link Confirmation</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to undo the link? This will revert the job card merge and restore the original PO job card.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowUndoModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                disabled={isUndoing}
              >
                Cancel
              </button>
              <button
                onClick={confirmUndoLink}
                className="px-4 py-2 text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                disabled={isUndoing}
              >
                {isUndoing ? 'Undoing...' : 'Confirm Undo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GRNs Panel - only show if there are GRNs to display and not an ATERA job card */}
      {jobCard?.order_number && !isAteraJobCard(jobCard) && (
        <GrnTable
          grns={grns}
          onEdit={handleEditGrn}
          onFinalize={handleFinalizeGrn}
          onDelete={handleDeleteGrn}
          onView={handleViewGrn}
          onCreateNew={() => setShowGrnForm(true)}
          showCreateButton={true}
          showActions={isAdmin}
          jobCardId={Number(id)}
          isAdmin={isAdmin}
        />
      )}

      {/* GRN Form Modal */}
      {showGrnForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
          <div className="max-w-4xl w-full mx-4 my-8">
            <GrnForm
              jobCardId={Number(id)}
              grn={editingGrn}
              onSave={() => handleGrnSave()}
              onClose={handleGrnCancel}
              isEditing={!!editingGrn}
            />
          </div>
        </div>
      )}

      {/* GRN Finalize Modal */}
      {finalizingGrn && (
        <GrnFinalizeModal
          grn={finalizingGrn}
          onFinalize={() => handleGrnFinalized()}
          onCancel={() => setFinalizingGrn(null)}
        />
      )}

      {/* GRN View Modal */}
      {viewingGrn && (
        <GrnViewModal
          grn={viewingGrn}
          isOpen={!!viewingGrn}
          onClose={() => setViewingGrn(null)}
        />
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
          Details
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-gray-600 dark:text-gray-400">Job Number</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {jobCard?.job_number}
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">{jobCard?.user?.utype === 'STORE' ? 'Supplier' : 'Store'}</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {jobCard?.user?.utype === 'STORE' ? jobCard.supplier?.name : jobCard?.store?.name}
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Date</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {jobCard?.date ? new Date(jobCard.date).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Time</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {jobCard?.time_in} - {jobCard?.time_out}
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Travel Distance</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {jobCard?.travel_distance} km
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400">Order Number</p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {jobCard?.order_number || "-"}
            </p>
          </div>
        </div>
      </div>



      {/* Embedded ATR Items section */}
      {atrItems.length > 0 && (
        <ATRItemsEmbedded
          atrItems={atrItems.map(item => ({
            ...item,
            equipment: item.equipment
              ? {
                  description: item.equipment.description,
                  serial_number: item.equipment.serial_number ?? undefined,
                  model_number: item.equipment.model_number ?? undefined,
                }
              : undefined,
          }))}
          isLoading={isLoadingATRItems}
          onSync={jobCard?.order_number ? handleSyncATRItems : undefined}
          isSyncing={isSyncingATRItems}
          showCompleteActions={false}
        />
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
          Tasks
        </h2>
        <div className="space-y-4">
          {tasks.length > 0 ? (
            tasks.map((task) => (
              <div
                key={task.id}
                className="border rounded-lg p-4 bg-white dark:bg-gray-700"
              >
                <div className="mb-4">
                  <p className="text-gray-700 dark:text-gray-300">{task.description}</p>
                  {task.equipment && task.equipment.length > 0 && (
                    <div className="mt-2">
                      <div 
                        className="font-medium text-gray-900 dark:text-white flex items-center cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-200 py-1.5 px-3 border border-gray-200 dark:border-gray-700 rounded-t-lg bg-gray-50 dark:bg-gray-800"
                        onClick={() => toggleEquipmentSection(task.id)}
                      >
                        {expandedEquipment[task.id] ? 
                          <ChevronDown className="w-4 h-4 mr-2 text-indigo-500" /> : 
                          <ChevronRight className="w-4 h-4 mr-2 text-indigo-500" />
                        }
                        Equipment Used
                      </div>
                      {expandedEquipment[task.id] && (
                        <div className="space-y-2 p-3 border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg bg-white dark:bg-gray-800">
                          {(() => {
                            // Group equipment by type, subtype, and order number
                            const groupedEquipment = task.equipment.reduce((acc, item) => {
                              const typeKey = item.equipment?.equipment_type?.name || 'Unknown Type';
                              const subtypeKey = item.equipment?.equipment_subtype_id && 
                                item.equipment?.equipment_type?.subtypes?.find(
                                  (s: { id: number }) => s.id === item.equipment?.equipment_subtype_id
                                )?.name || undefined;
                              
                              const key = `${typeKey}${subtypeKey ? `:${subtypeKey}` : ''}`;
                              
                              if (!acc[key]) {
                                acc[key] = {
                                  groups: {},
                                  type: typeKey,
                                  subtype: subtypeKey
                                };
                              }
                              
                              // Further group by model and order number
                              const modelKey = `${item.equipment?.name || ''}|${item.equipment?.model_number || ''}|${item.order_number || ''}|${item.allocation_type || ''}|${item.store_number || ''}|${item.allocated_id || ''}`;
                              
                              if (!acc[key].groups[modelKey]) {
                                acc[key].groups[modelKey] = {
                                  item: {...item},
                                  count: 0
                                };
                              }
                              
                              // Increment count
                              acc[key].groups[modelKey].count += (item.quantity || 1);
                              
                              return acc;
                            }, {} as Record<string, {
                              groups: Record<string, {
                                item: TaskEquipment,
                                count: number
                              }>,
                              type: string,
                              subtype?: string
                            }>);
                            
                            return Object.entries(groupedEquipment).map(([key, group]) => (
                            <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                              <div className="bg-gray-100 dark:bg-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                                {group.type}{group.subtype ? ` > ${group.subtype}` : ''}
                              </div>
                              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {Object.values(group.groups).map((groupItem, idx) => (
                                  <div key={idx} className="p-2 bg-white dark:bg-gray-700">
                                    <p className="text-gray-600 dark:text-gray-400 flex items-start gap-2">
                                      <span className="font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-xs">
                                          {groupItem.count}x
                                      </span>
                                      <span>
                                          {groupItem.item.equipment?.name}
                                          {groupItem.item.equipment?.serial_number && (
                                          <span className="text-gray-500 dark:text-gray-400">
                                              {" - "}S/N: {groupItem.item.equipment.serial_number}
                                          </span>
                                        )}
                                          {groupItem.item.equipment?.model_number && (
                                          <span className="text-gray-500 dark:text-gray-400">
                                              {" - "}Model: {groupItem.item.equipment.model_number}
                                          </span>
                                        )}
                                          {groupItem.item.order_number && (
                                          <span className="ml-1">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100">
                                                Order #{groupItem.item.order_number}
                                            </span>
                                          </span>
                                        )}
                                          {groupItem.item.allocation_type === 'store' ? (
                                          <span className="text-gray-500 dark:text-gray-400">
                                              {" - "}Store: {groupItem.item.store_number}
                                          </span>
                                        ) : (
                                          <span className="text-gray-500 dark:text-gray-400">
                                              {" - "}Allocated to Equipment: #{groupItem.item.allocated_id}
                                          </span>
                                        )}
                                      </span>
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {task.pictures && task.pictures.length > 0 && (
                  <div className="relative mt-2">
                    <div className="mb-4">
                      <ImageDebugInfo
                        images={task.pictures.map(pic => ({
                          id: pic.id,
                          url: getTaskImageUrl(pic.picture),
                          alt: `Task ${task.id} picture`
                        }))}
                        type="task"
                      />
                      <TaskImagesGallery
                        images={task.pictures.map(pic => ({
                          id: pic.id,
                          url: getTaskImageUrl(pic.picture),
                          alt: `Task ${task.id} picture`
                        }))}
                        readOnly={true}
                      />
                      

                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No tasks added</p>
          )}
        </div>
      </div>

      {/* Only show Stock Request section if there's equipment in any task */}
      {tasks.some((task) => task.equipment && task.equipment.length > 0) && (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-6">
        <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
          Stock Request
        </h2>
        <div className="flex justify-center">
            {jobCard?.stock_request === 0 ? (
              <button
                onClick={() => setIsStockRequestModalOpen(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-400"
              >
                Request Stock Replenishment
              </button>
            ) : (
              <div className="text-center">
                <div className="text-green-600 dark:text-green-400 font-semibold mb-1">
                  Stock Requested
                </div>
                {jobCard?.stock_request_date && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Requested on{" "}
                    {new Date(jobCard.stock_request_date).toLocaleDateString(
                      "en-ZA",
                      { day: "2-digit", month: "short", year: "numeric" }
                    )}{" "}
                    at{" "}
                    {new Date(jobCard.stock_request_date).toLocaleTimeString(
                      "en-ZA",
                      { hour: "2-digit", minute: "2-digit" }
                    )}
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
          Signatures
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {signatures.length > 0 ? (
            signatures.map((signature) => (
              <div
                key={signature.id}
                className="border rounded-lg p-4 bg-white dark:bg-gray-700"
              >
                <p className="font-semibold text-gray-900 dark:text-white">
                  {signature.sign_name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {signature.is_manager ? (jobCard?.user?.utype === 'STORE' ? 'Supplier' : 'Manager') : (jobCard?.user?.utype === 'STORE' ? 'Manager' : 'Technician')}
                </p>
                {/* Show supplier name for supplier signatures */}
                {jobCard?.user?.utype === 'STORE' && signature.is_manager && jobCard?.supplier?.name && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">
                    Supplier: {jobCard.supplier.name}
                  </p>
                )}
                <img
                  src={getSignatureImageUrl(signature.picture)}
                  alt="Signature"
                  className="max-h-20"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    // Show a fallback message
                    const fallback = document.createElement('div');
                    fallback.className = 'text-gray-500 text-sm';
                    fallback.textContent = 'Signature image not available';
                    target.parentNode?.appendChild(fallback);
                  }}
                />
              </div>
            ))
          ) : (
            <p className="text-gray-500 dark:text-gray-400">
              No signatures added
            </p>
          )}
        </div>
      </div>

      <TaskImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        tasks={tasksWithImages}
        currentIndex={currentImageIndex}
        onNext={handleNextImage}
        onPrev={handlePrevImage}
      />

      <StockRequestModal
        isOpen={isStockRequestModalOpen}
        onClose={() => setIsStockRequestModalOpen(false)}
        onSuccess={refreshJobCard}
        equipment={tasks
          .filter((task) => task.equipment && task.equipment.length > 0)
          .flatMap((task) =>
            task.equipment!.map((eq) => {
              const equipment = eq.equipment;
              if (!equipment) return undefined;

              const mappedEquipment: Equipment = {
                equipment: {
                  name: equipment.name || "",
                  type: equipment.equipment_type?.name || "",
                  subtype:
                    equipment.equipment_type?.subtypes?.find(
                      (s: { id: number }) => s.id === equipment.equipment_subtype_id
                    )?.name || "",
                  equipment_subtype_id: equipment.equipment_subtype_id || 0,
                  equipment_type: equipment.equipment_type
                    ? {
                        id: equipment.equipment_type.id,
                        name: equipment.equipment_type.name,
                        subtypes: equipment.equipment_type.subtypes?.map(
                          (st) => ({
                            id: st.id,
                            name: st.name,
                            equipment_type_id: equipment.equipment_type!.id,
                          })
                        ),
                      }
                    : undefined,
                },
                quantity: 1,
              };
              return mappedEquipment;
            })
          )
          .filter((item): item is Equipment => item !== undefined)}
        jobNumber={jobCard?.job_number || ""}
        storeNumber={jobCard?.store?.storenumber || ""}
        userEmail={jobCard?.user?.email || ""}
      />
    </div>
  );
}
